import { useEffect, useState } from 'react';

/**
 * Loads page-specific stylesheets while a component is mounted and removes them
 * on unmount. Used to scope the whiteboard CSS (skeleton.css/styles.css) to the
 * dashboard only — loading them globally bleeds into the auth pages (e.g. hides
 * the login agreement checkbox). hrefs are absolute /static/... paths so url()
 * references inside the CSS resolve correctly.
 *
 * Returns whether the requested sheets have finished loading (all <link>s fired
 * `load`/`error`, or a `.sheet` is already parsed). Callers that gate their
 * first paint on route CSS use this to avoid a flash of unstyled content — the
 * route CSS is injected AFTER React mounts, so without a gate the header popups
 * and the unstyled form paint as raw text in the load gap. Legacy loaded all
 * CSS render-blocking in <head>; the boolean lets a page mirror that. Existing
 * callers ignore the return value and behave exactly as before.
 */
export function useStylesheets(hrefs: string[]): boolean {
  const sig = hrefs.join('|');
  // Track the signature we've finished loading; `loaded` is true only for the
  // CURRENT sig, so a route change (new hrefs) re-blocks until the new sheets
  // load rather than reporting the previous route's stale "loaded" state.
  const [loadedSig, setLoadedSig] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const finish = () => {
      if (!cancelled) setLoadedSig(sig);
    };
    if (hrefs.length === 0) {
      finish();
      return undefined;
    }
    let remaining = hrefs.length;
    const done = () => {
      remaining -= 1;
      if (remaining <= 0) finish();
    };
    const links = hrefs.map((href) => {
      const existing = document.querySelector(
        `link[data-page-css="${href}"]`
      ) as HTMLLinkElement | null;
      if (existing) {
        // Already in the document (e.g. a still-mounted sibling). Ready if its
        // sheet has parsed (same idiom as AhuCanvas.waitForPageCss); else wait.
        if (existing.sheet) done();
        else {
          existing.addEventListener('load', done, { once: true });
          existing.addEventListener('error', done, { once: true });
        }
        return existing;
      }
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.setAttribute('data-page-css', href);
      // Resolve on error too so a 404 can't wedge a gated page on a blank frame.
      link.addEventListener('load', done, { once: true });
      link.addEventListener('error', done, { once: true });
      document.head.appendChild(link);
      return link;
    });
    return () => {
      cancelled = true;
      links.forEach((l) => l.parentNode?.removeChild(l));
    };
  }, [sig]); // eslint-disable-line react-hooks/exhaustive-deps

  return loadedSig === sig;
}
